/*
 * Copyright (c) 2016-present, Parse, LLC
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import LoaderContainer                    from 'components/LoaderContainer/LoaderContainer.react';
import Button                             from 'components/Button/Button.react';
import FlowFooter                         from 'components/FlowFooter/FlowFooter.react';
import styles                             from 'components/FlowFooter/FlowFooter.scss';
import PropTypes                          from 'prop-types';
import React                              from 'react';
import SaveButton                         from 'components/SaveButton/SaveButton.react';

import PushAudienceEveryoneDialog         from 'components/PushAudienceEveryoneDialog/PushAudienceEveryoneDialog.react';

export default class FlowView extends React.Component {
  constructor(props) {
    super();

    this.state = {
      changes: props.initialChanges || {},
      saveState: SaveButton.States.WAITING,
      saveError: '',
      showPushAudienceEveryoneDialog: false,
      confirmPushAudienceEveryoneDialog: false
    };
  }

  componentWillReceiveProps(props, context) {
    let newChanges = {...this.state.changes};
    for (let k in props.initialFields) {
      if (this.state.changes[k] === props.initialFields[k]) {
        delete newChanges[k];
      }
    }
    this.setState({changes: newChanges});
  }

  onSaveClickHandler(onSubmit, fields, changes, setField, resetFields, afterSave) {
    this.setState({ saveState: SaveButton.States.SAVING });

    onSubmit({ changes, fields, setField, resetFields }).then(() => {
      this.setState({ saveState: SaveButton.States.SUCCEEDED });
      afterSave({ fields, setField, resetFields });
    }).catch(({ message, error, notice, errors = [] }) => {
      this.setState({
        saveState: SaveButton.States.FAILED,
        saveError: errors.join(' ') || message || error || notice || 'An error occurred',
      });
    });
  }

  setField(key, value, preserveSavingState = false) {
    if (this.state.saveState !== SaveButton.States.SAVING) {
      let newChanges = {...this.state.changes};
      newChanges[key] = value;
      if (newChanges[key] === this.props.initialFields[key]) {
        delete newChanges[key];
      }
      //Modify stored state in case component recieves new props,
      //as componentWillReceiveProps would otherwise clobber this change.
      this.state.changes[key] = value;
      this.setState({
        saveState: preserveSavingState ? this.state.saveState : SaveButton.States.WAITING,
        saveError: '',
        changes: newChanges,
      });
    }
  }

  currentFields() {
    let fields = {};
    for (let k in this.props.initialFields) {
      fields[k] = this.props.initialFields[k];
    }
    for (let k in this.state.changes) {
      fields[k] = this.state.changes[k];
    }
    return fields;
  }

  resetFields() {
    if (this.state.saveState !== SaveButton.States.SAVING) {
      this.setState({
        saveState: SaveButton.States.WAITING,
        saveError: '',
        changes: this.props.initialChanges || {},
      });
    }
  }

  render() {
    let {
      inProgressText,
      submitText,
      showFooter = () => true,
      footerContents,
      succeededText,
      defaultFooterMessage,
      renderForm,
      validate = () => '',
      onSubmit,
      afterSave = () => {},
      secondaryButton = () => {},
    } = this.props;
    let {
      changes,
      saveState,
      saveError
    } = this.state;
    let setField = this.setField.bind(this);
    let resetFields = this.resetFields.bind(this);
    let fields = this.currentFields();
    let form = renderForm({ fields, changes, setField, resetFields });

    let invalidFormMessage = validate({ changes, fields });
    let hasFormValidationError = React.isValidElement(invalidFormMessage) || (invalidFormMessage && invalidFormMessage.length > 0);
    let errorMessage = '';
    let footerMessage = null;
    let shouldShowFooter = showFooter(changes);

    let hideFooterAnimation = null;

    if (saveState === SaveButton.States.FAILED) {
      errorMessage = saveError;
      shouldShowFooter = true;
    } else if (saveState === SaveButton.States.SUCCEEDED) {
      shouldShowFooter = true;
      hideFooterAnimation = true;
    } else if (invalidFormMessage === 'use default') {
      footerMessage = defaultFooterMessage;
      shouldShowFooter = true;
    } else if (hasFormValidationError) {
      errorMessage = invalidFormMessage;
      shouldShowFooter = true;
    } else if (shouldShowFooter) {
      shouldShowFooter = Object.keys(changes).length > 0;
      footerMessage = shouldShowFooter ? footerContents({ changes, fields }) : '';
    }

    let saveButton = <SaveButton
      state={saveState}
      waitingText={submitText}
      savingText={inProgressText}
      succeededText={succeededText}
      disabled={!!hasFormValidationError}
      onClick={() => {
        if(changes.audience_id === "everyone") {
          this.setState({showPushAudienceEveryoneDialog: true})
        } else {
          this.onSaveClickHandler(onSubmit, fields, changes, setField, resetFields, afterSave);
        }
      }}
    />;

    let footer = <FlowFooter
      shouldShowFooter={shouldShowFooter}
      primary={saveButton}
      secondary={secondaryButton({ setField })}
      errorMessage={errorMessage}>
      {footerMessage}
    </FlowFooter>;

    let classes = [shouldShowFooter ? styles.IsFooterShow : null]

    return (
      <div className={classes.join(' ')}>
        <LoaderContainer close={true} inner={true} specialPages={this.props.specialPages}>
          {form}
        </LoaderContainer>
        {
          this.state.showPushAudienceEveryoneDialog
            ? <PushAudienceEveryoneDialog
                onCancel={() => this.setState({ showPushAudienceEveryoneDialog: false })}
                onConfirm={() => {
                  this.onSaveClickHandler(onSubmit, fields, changes, setField, resetFields, afterSave)
                  this.setState({ showPushAudienceEveryoneDialog: false })
                }}
              />
            : null
        }
        {footer}
      </div>
    );
  }
}
